import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, throwError } from "rxjs";
import { ConfigService } from "./config.service";
import { catchError, filter, switchMap, take } from "rxjs/operators";
import { ModalCloseService } from "./modal-close.service";

@Injectable({
  providedIn: 'root'
})
export class HttpClientWrapperService {
  private headers: HttpHeaders;
  clientId: string | null;

  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);

  constructor(
    private httpClient: HttpClient,
    private config: ConfigService,
    private modal: ModalCloseService,
  ) {
    this.setHeaders()
  }

  readonly allowedClientId = '65c08d3492217b62947b8856'; //EKAM

  isSpecialClient(): boolean {
    return localStorage.getItem('clientId') === this.allowedClientId;
  }

  setHeaders() {
    this.headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'api-key': this.config.json.api_key,
      'Authorization': 'Bearer ' + localStorage.getItem('token'),
      'Content-APP-DATA': JSON.stringify({
        appVersion: "",
        groupId: this.config.json.group_id,
        authToken: localStorage.getItem('token'),
        clientId: localStorage.getItem('clientId'),
        deviceId: "",
        deviceImei: "",
        deviceModel: "",
        deviceOs: "",
        deviceType: "Web",
        franchiseId: "",
        location: "",
        macAddress: "",
        networkType: "",
        operatorId: localStorage.getItem('operatorId'),
        osVersion: "",
        profileId: "",
        resolution: "",
        subscriberId: "",
        serviceId: 'OTT',
        adminEmail: localStorage.getItem('email'),
        admin: localStorage.getItem('adminInfo')
      })
    });
  }

  private addHeaders(options?: any): any {
    if (!options) {
      options = {};
    }
    if (!options.headers) {
      options.headers = this.headers;
    } else {
      options.headers = options.headers.append('Content-APP-DATA', this.headers.get('Content-APP-DATA'));
    }
    return options;
  }

  private is401(error: any): boolean {
    return error?.status === 401 || error?.error?.statusCode === 401;
  }

  private handleHttpError(error: any, retryFn: () => Observable<any>): Observable<any> {
    if (this.is401(error)) {
      return this.handle401Error(retryFn);
    }
    return throwError(error);
  }

  private handle401Error(retryFn: () => Observable<any>): Observable<any> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const tokenid = localStorage.getItem('refreshTokenId');
      const body = { adminRefreshTokenId: tokenid };
      const url = this.config.json.apiEndpoint + 'adminRefreshToken/adminTokenInfo';

      return this.postWithoutHeader(url, body).pipe(
        switchMap((data) => {
          this.isRefreshing = false;
          if (data.statusCode === 401) {
            this.refreshTokenSubject.next(null);
            this.modal.updateShowModal(true);
            return throwError(new Error('Session expired'));
          }
          const newToken = data.data.token;
          localStorage.setItem('token', newToken);
          localStorage.setItem('refreshTokenId', data.data.refreshTokenId);
          if (data.data.tokenExpiry) {
            localStorage.setItem('tokenExpiryTime', data.data.tokenExpiry);
          }
          this.refreshTokenSubject.next(newToken);
          return retryFn();
        }),
        catchError((err) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(null);
          this.modal.updateShowModal(true);
          return throwError(err);
        })
      );
    }

    // Another request already triggered a refresh — wait for it to complete then retry
    return this.refreshTokenSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap(() => retryFn())
    );
  }

  get(path: any, options?: any): Observable<any> {
    this.setHeaders();
    return this.httpClient.get(path, this.addHeaders(options)).pipe(
      catchError((err) => this.handleHttpError(err, () => {
        this.setHeaders();
        return this.httpClient.get(path, this.addHeaders(options));
      }))
    );
  }

  post(path: any, body: any, options?: any): Observable<any> {
    this.setHeaders();
    return this.httpClient.post(path, body, this.addHeaders(options)).pipe(
      catchError((err) => this.handleHttpError(err, () => {
        this.setHeaders();
        return this.httpClient.post(path, body, this.addHeaders(options));
      }))
    );
  }

  postWithoutHeader(path: any, body: any): Observable<any> {
    return this.httpClient.post(path, body);
  }

  getWithoutHeader(path: any, options?: any): Observable<any> {
    return this.httpClient.get(path);
  }

  put(path: any, body: any, options?: any): Observable<any> {
    this.setHeaders();
    return this.httpClient.put(path, body, this.addHeaders(options)).pipe(
      catchError((err) => this.handleHttpError(err, () => {
        this.setHeaders();
        return this.httpClient.put(path, body, this.addHeaders(options));
      }))
    );
  }

  patch(path: any, body: any, options?: any): Observable<any> {
    this.setHeaders();
    return this.httpClient.patch(path, body, this.addHeaders(options)).pipe(
      catchError((err) => this.handleHttpError(err, () => {
        this.setHeaders();
        return this.httpClient.patch(path, body, this.addHeaders(options));
      }))
    );
  }

  delete(path: any, options?: any): Observable<any> {
    this.setHeaders();
    return this.httpClient.delete(path, this.addHeaders(options)).pipe(
      catchError((err) => this.handleHttpError(err, () => {
        this.setHeaders();
        return this.httpClient.delete(path, this.addHeaders(options));
      }))
    );
  }
}
